import { Flex, FormGroup, FormLabel } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { IConsultantProfile, IContactPerson, ISharedProfile } from "@/data/consultant-profiles";
import { IChangeable, Text } from "@/eworkitComponents";
import { IWithFormikFieldProps, withFormikField } from "@/formikComponents";
import { FormikCodelistManagersSelect } from "@/views/components/Formik";
import { Messenger } from "@/views/components/Messenger";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export const validationContactPersonSectionSchema = Yup.object().shape({
  contactPerson: Yup.object().nullable(true).required(VALIDATION_TYPES.REQUIRED),
});

export interface IContactPersonFormSectionValues {
  contactPerson: IContactPerson;
  readonly sharedProfile: ISharedProfile;
}

interface IContactPersonFormSectionProps extends IChangeable, IWithFormikFieldProps {
  value: IContactPersonFormSectionValues;
}

export const EditableContactPersonFormSection: React.FunctionComponent<IContactPersonFormSectionProps> = React.memo(
  ({ name, value }) => {
    const { t } = useTranslation("session");

    const { contactPerson, sharedProfile } = value;

    return (
      <Flex gap="xs" flexDirection="column">
        <Flex flexDirection="column" flexGrow={1}>
          <FormGroup
            label={<FormLabel label={t("CONSULTANT_PROFILES.DETAIL.CONTACT_PERSON.LABEL")} required />}
            error={<FormikError name={name} data-test={`error-${name}`} />}>
            <FormikCodelistManagersSelect
              name={name}
              testName={name}
              searchParams={{
                companyId: sharedProfile.company.id,
              }}
              value={contactPerson}
            />
          </FormGroup>
        </Flex>
        {contactPerson && <Text color="secondary" text={contactPerson.email} />}
      </Flex>
    );
  },
);

export const FormikEditableContactPersonFormSection = withFormikField(EditableContactPersonFormSection);

export const CONTACT_MESSENGER_CONTEXT = "profile-contact-form";

interface IContactPersonFormProps extends IEditableSectionRenderProps {
  source: IConsultantProfile;
  form: FormikProps<IContactPersonFormSectionValues>;
}

export const ContactPersonForm: React.FunctionComponent<IContactPersonFormProps> = React.memo(props => {
  const { form, editableProps } = props;
  useEditableSubmit(form, editableProps);

  return (
    <Flex flexGrow={1} flexDirection="column" gap="md">
      <Messenger context={CONTACT_MESSENGER_CONTEXT} />
      <Flex flexDirection="column">
        <FormikEditableContactPersonFormSection name="contactPerson" value={form.values} />
      </Flex>
    </Flex>
  );
});

interface IContactPersonFormikProps
  extends IProfileFormikProps<IContactPersonFormSectionValues>,
    IEditableSectionRenderProps {
  source: IConsultantProfile;
}

export const ContactPersonFormik: React.FunctionComponent<IContactPersonFormikProps> = React.memo(
  ({ initialValues, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationContactPersonSectionSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<IContactPersonFormSectionValues>) => <ContactPersonForm form={form} {...rest} />}
    />
  ),
);

export const ContactPersonEdit = profilePartialUpdaterBuilder(ContactPersonFormik, {
  validationSchema: validationContactPersonSectionSchema,
  messengerContext: CONTACT_MESSENGER_CONTEXT,
});
